import type { MediaUrls } from "@/lib/types";

/** Longest-edge pixel width of each stored variant (see image-processor). */
const VARIANT_WIDTH: Record<keyof MediaUrls, number> = {
  thumb: 320,
  sm: 640,
  md: 1280,
  lg: 2048,
};

/** Build a `srcset` string from the four presigned variant URLs. */
export function buildSrcSet(urls: MediaUrls): string {
  return (Object.keys(VARIANT_WIDTH) as (keyof MediaUrls)[])
    .map((v) => `${urls[v]} ${VARIANT_WIDTH[v]}w`)
    .join(", ");
}

/** Region display names per locale, built lazily and cached. */
const regionNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function regionNamesFor(locale: string): Intl.DisplayNames | null {
  if (typeof Intl === "undefined" || !("DisplayNames" in Intl)) return null;
  if (!regionNamesByLocale.has(locale)) {
    try {
      regionNamesByLocale.set(locale, new Intl.DisplayNames([locale], { type: "region" }));
    } catch {
      regionNamesByLocale.set(locale, null);
    }
  }
  return regionNamesByLocale.get(locale) ?? null;
}

/**
 * Localized country name from an ISO country code (falls back to the code).
 * The locale is required: every call site renders user-facing text and must
 * pass the active UI locale.
 */
export function countryName(code: string, locale: string): string {
  try {
    return regionNamesFor(locale)?.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Translator over the deck-namespace distance keys shared by browse/deck. */
type DistanceTranslator = (key: "sameCity" | "kmAway", values?: { km: number }) => string;

/**
 * Card distance label from a city-centre `distanceKm`, resolved through the
 * message catalogs (`deck.sameCity` / `deck.kmAway`). Null when distance is
 * unknown (either side lacks a city).
 */
export function distanceLabel(distanceKm: number | null, t: DistanceTranslator): string | null {
  if (distanceKm == null) return null;
  if (distanceKm <= 0) return t("sameCity");
  return t("kmAway", { km: distanceKm });
}
